import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { useState, useEffect, createContext, useContext } from "react";
import Login from "./Pages/Login";
import Home from "./Pages/Home";
import ScrollToTop from "./Pages/ScrollToTop";
import "./App.scss";
import Products from "./Pages/SoftwareUpdate/Products";
import { ToastContainer } from "react-toastify";
import Sidebar from "./Components/Sidebar/Sidebar";
import Navbar from "./Components/Navbar/Navbar";
import axios from "axios";
import Cookies from "js-cookie";
import BugReports from "./Pages/BugReport/BugReports";
import ScheduledAutomationPage from "./Pages/scheduled-automations/ScheduledAutomationsPage";

// Authentication Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Layout Component for authenticated pages
const AuthenticatedLayout = ({ children }) => {
  return (
    <>
      {/* Fixed Header */}
      <header className="app__header">
        <Navbar />
      </header>

      {/* Layout Container */}
      <div className="app__layout">
        {/* Sidebar */}
        <aside className="app__sidebar">
          <Sidebar />
        </aside>

        {/* Main Content */}
        <main className="app__main">
          <div className="main-content">{children}</div>
        </main>
      </div>
    </>
  );
};

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check for existing authentication on app load
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      setLoading(true);
      // Check if user has valid JWT token in sessionStorage
      const jwtToken = sessionStorage.getItem("jwtToken");
      const username =
        sessionStorage.getItem("username") || localStorage.getItem("username");

      if (jwtToken) {
        try {
          // Parse and validate token structure (basic JWT validation)
          const tokenParts = jwtToken.split(".");
          if (tokenParts.length === 3) {
            // Decode JWT payload (without verification for demo)
            const payload = JSON.parse(atob(tokenParts[1]));
            const currentTime = Math.floor(Date.now() / 1000);

            // Check if token is expired
            if (payload.exp && payload.exp > currentTime) {
              setIsAuthenticated(true);
              setUser({
                username: username || payload.username || payload.sub,
                email: payload.email || `${username}@example.com`,
                role: payload.role || "user",
              });
              console.log("User authenticated from session");
            } else {
              console.log("Token expired, clearing session");
              throw new Error("Token expired");
            }
          } else {
            // If not a proper JWT, still allow for backward compatibility
            setIsAuthenticated(true);
            setUser({
              username: username,
              email: `${username}@example.com`,
              role: "user",
            });
            console.log("User authenticated (non-JWT token)");
          }
        } catch (parseError) {
          console.log("Error parsing token:", parseError);
          throw parseError;
        }
      } else {
        console.log("No token found in session");
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      // Clear invalid tokens
      sessionStorage.removeItem("jwtToken");
      sessionStorage.removeItem("username");
      localStorage.removeItem("username");
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      setLoading(true);

      // Encode username and password with Base64
      const encodedCredentials = btoa(
        `${credentials.username}:${credentials.password}`,
      );

      const response = await axios.post(
        `${process.env.REACT_APP_URL}/master-login`,
        {}, // No body needed for this request
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${encodedCredentials}`,
            "X-ProductID": process.env.REACT_APP_PRODUCT_ID,
          },
          withCredentials: true, // Ensures cookies are sent with the request
        },
      );

      if (response.status === 200) {
        const jwtToken = response.headers["authorization"]; // Get access token
        sessionStorage.setItem("jwtToken", jwtToken);

        // If "Remember Me" is checked, store username in localStorage
        if (credentials.rememberMe) {
          localStorage.setItem("username", credentials.username);
        } else {
          sessionStorage.setItem("username", credentials.username);
        }

        // ✅ Retrieve Refresh Token from cookies (if set by backend)
        const refreshToken = Cookies.get("refreshToken");

        if (refreshToken) {
          Cookies.set("refreshToken", refreshToken, {
            expires: credentials.rememberMe ? 7 : undefined, // 7 days if "Remember Me" is checked
            secure: true,
            sameSite: "Strict",
          });
        }

        // Set user data
        const userData = {
          username: credentials.username,
          email: credentials.email || `${credentials.username}@example.com`,
          role: "user",
        };

        setIsAuthenticated(true);
        setUser(userData);

        return { success: true, user: userData };
      } else {
        return { success: false, error: "Login failed: Invalid response." };
      }
    } catch (error) {
      console.error("Login failed:", error);
      return {
        success: false,
        error:
          error.response?.data?.message ||
          "Username or Password is wrong. Please try again.",
      };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    // Clear auth data
    sessionStorage.removeItem("jwtToken");
    sessionStorage.removeItem("username");
    localStorage.removeItem("username");

    // Clear refresh token cookie
    Cookies.remove("refreshToken");

    setIsAuthenticated(false);
    setUser(null);
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    checkAuthStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

function App() {
  return (
    <div className="app">
      <Router>
        <AuthProvider>
          <ScrollToTop />

          <Routes>
            <Route path="/" element={<Navigate to="/login" />} />
            <Route path="/login" element={<Login />} />
            <Route
              path="/home/products"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <Products />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/home/bug-reports"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <BugReports />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/home/scheduled-automations"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <ScheduledAutomationPage />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/home/*"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <Home />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
          </Routes>

          {/* Toast Notifications */}
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            className="app__toast"
          />
        </AuthProvider>
      </Router>
    </div>
  );
}

export default App;
